import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Channel,
  GET_CHANNELS,
  GET_INNOVATIONS_FOR_ADMIN,
  GET_INTRAPRENEUR_CHALLENGE_BY_CHANNEL_ID,
  Innovation,
} from '@frontend/data-access';
import { Form, Select } from 'antd';
import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';

interface ChannelSelectorProps {
  selectedChannel?: string;
  selectedChallenge?: string;
  selectedInnovationSpace?: string;
  onChannelChange: (value: string) => void;
  onChallengeChange: (value?: string) => void;
  onInnovationSpaceChange: (value?: string) => void;
}

export function ChannelSelector({
  selectedChannel,
  selectedChallenge,
  selectedInnovationSpace,
  onChannelChange,
  onChallengeChange,
  onInnovationSpaceChange,
}: ChannelSelectorProps) {
  const { t } = useTranslation('common');
  const { data: channelTags } = useQuery<{ channels: Channel[] }>(GET_CHANNELS);
  const { data: innovationSpaces, loading: loadingInnovationSpaces } =
    useQuery<{
      listInnovationSpace: {
        innovationSpaces: Innovation[];
        total: number;
      };
    }>(GET_INNOVATIONS_FOR_ADMIN, {
      variables: {
        page: 1,
        size: 1000,
      },
    });

  const [
    fetchChallenges,
    { data: challengesByChannel, loading: loadingChallenges },
  ] = useLazyQuery<{
    challengesByChannel: { id: string; title: string }[];
  }>(GET_INTRAPRENEUR_CHALLENGE_BY_CHANNEL_ID, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (channelTags?.channels && channelTags.channels.length > 0) {
      onChannelChange(channelTags.channels[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelTags]);

  useEffect(() => {
    if (selectedChannel) {
      fetchChallenges({
        variables: {
          channelId: selectedChannel,
        },
      });
    }
  }, [fetchChallenges, selectedChannel]);

  return (
    <Form
      layout="vertical"
      className="flex gap-4 bg-gray-100 p-4 rounded-2xl items-center"
    >
      <Form.Item label={t('select_idea_channel')} className="mb-0">
        <Select
          className="!w-[250px]"
          value={selectedChannel}
          placeholder={t('select_idea_channel')}
          onChange={(value) => {
            onChannelChange(value);
            onChallengeChange(undefined);
            onInnovationSpaceChange(undefined);
          }}
          options={channelTags?.channels.map((channel) => ({
            value: channel.id,
            label: channel.title,
          }))}
        />
      </Form.Item>
      <Form.Item label={t('select_challenge')} className="mb-0">
        <Select
          className="!w-[250px]"
          placeholder={t('challenges')}
          value={selectedChallenge}
          onChange={(value) => onChallengeChange(value)}
          options={challengesByChannel?.challengesByChannel.map(
            (challenge) => ({
              value: challenge.id,
              label: challenge.title,
            })
          )}
          loading={loadingChallenges}
          disabled={challengesByChannel?.challengesByChannel.length === 0}
          allowClear
        />
      </Form.Item>
      <Form.Item
        label={t('idea_filters_select_innovation_space')}
        className="mb-0"
      >
        <Select
          className="!w-[250px]"
          placeholder={t('idea_filters_innovation_space')}
          value={selectedInnovationSpace}
          onChange={(value) => onInnovationSpaceChange(value)}
          options={innovationSpaces?.listInnovationSpace.innovationSpaces.map(
            (item) => ({
              value: item.id,
              label: item.name,
            })
          )}
          loading={loadingInnovationSpaces}
          allowClear
        />
      </Form.Item>
    </Form>
  );
}
